import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CreditCard } from "lucide-react";
import { parseTs } from "../api/labels";
import { isPlatform, platformLabel as label } from "../api/platforms";
import { type AnyColumnDef, DataTable, createColumnHelper } from "../components/DataTable";
import { useCurrentUser } from "../auth/me";
import { useToast } from "../components/toast";
import { InfoDot, PRESSURE_HELP } from "../components/InfoDot";
import { ProxyUsage } from "../components/ProxyUsage";
import { Badge, Button, Dot, ErrorState, PageHeader, Panel, Row, Stack, Tag } from "@akaike/ui";
import { toneVariant } from "../lib/badge-tone";
import { Seg } from "../components/foundation";
import { AnimatedNumber } from "../lib/motion";
import {
  useCountryPreferenceV1ProxyCountryPreferenceGet,
  useSetCountryPreferenceV1ProxyCountryPreferencePlatformPut,
  useSummaryV1ProxySummaryGet,
  useProxiesV1ProxyProxiesGet,
  useEventsV1ProxyEventsGet,
  useSyncV1ProxySyncPost,
  useResetPoolV1ProxyResetPost,
  useMarkDeadV1ProxyProxiesPlatformIpMarkDeadPost,
  useReviveV1ProxyProxiesPlatformIpRevivePost,
  useResetScoreV1ProxyProxiesPlatformIpResetScorePost,
} from "../api/generated/qcsControlPlane";
import type {
  CountryCount,
  Platform,
  PlatformPool,
  ProxyCountryPreferenceOut,
  ProxyRow,
} from "../api/generated/model";

const fmtScore = (s: number | null) => (s == null ? "—" : Math.round(s).toString());
const fmtPct = (r: number | null) => (r == null ? "—" : `${(r * 100).toFixed(1)}%`);
const fmtMs = (m: number | null) => (m == null ? "—" : `${Math.round(m)} ms`);
const fmtRps = (r: number | null) => (r == null ? "—" : r.toFixed(1));
// Leases-per-IP Gini: 0 = perfectly even rotation, -> 1 = same few IPs hammered.
const fmtGini = (g: number | null) => (g == null ? "—" : g.toFixed(2));

function ago(iso: string | null): string {
  if (!iso) return "—";
  const s = Math.max(0, (Date.now() - parseTs(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function pressureZone(p: number): "ok" | "warn" | "crit" {
  if (p >= 1.0) return "crit";
  if (p >= 0.8) return "warn";
  return "ok";
}

// -- pressure gauge (semicircle, 0-1.2 scale) --------------------------------
function Gauge({ value }: { value: number }) {
  const zone = pressureZone(value);
  const color = `var(--${zone})`;
  const arc = 251.2; // π·80
  const frac = Math.min(1, Math.max(0, value / 1.2));
  const angle = (180 - frac * 180) * (Math.PI / 180);
  const nx = 100 + 80 * Math.cos(angle);
  const ny = 100 - 80 * Math.sin(angle);
  return (
    <svg viewBox="0 0 200 116" style={{ width: 130, height: 76, flex: "none" }}>
      <path d="M20,100 A80,80 0 0 1 180,100" fill="none" stroke="var(--line)" strokeWidth="14" strokeLinecap="round" />
      <path
        d="M20,100 A80,80 0 0 1 180,100"
        fill="none"
        stroke={color}
        strokeWidth="14"
        strokeLinecap="round"
        strokeDasharray={`${(frac * arc).toFixed(1)} ${arc}`}
        opacity="0.9"
      />
      <line x1="100" y1="100" x2={nx.toFixed(1)} y2={ny.toFixed(1)} stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="100" cy="100" r="5.5" fill="var(--panel)" stroke={color} strokeWidth="2.5" />
      <text x="14" y="114" fontFamily="var(--f-mono)" fontSize="9" fill="var(--ink-4)">0</text>
      <text x="88" y="28" fontFamily="var(--f-mono)" fontSize="9" fill="var(--ink-4)">1.0</text>
      <text x="172" y="114" fontFamily="var(--f-mono)" fontSize="9" fill="var(--ink-4)">1.2</text>
    </svg>
  );
}

function PlatformCard({ s }: { s: PlatformPool }) {
  const zone = pressureZone(s.pressure);
  const denom = Math.max(1, s.total);
  const pct = (n: number) => `${((n / denom) * 100).toFixed(1)}%`;
  const leasedActive = s.leased;
  const pillText = zone === "crit" ? "starvation" : zone === "warn" ? "pressure warn" : "healthy";
  return (
    <Panel alarm={zone === "crit"}>
      <Panel.Head>
        <span className={`plat ${s.platform}`}>{label(s.platform)}</span>
        <div className="flex-1" />
        <Badge variant={toneVariant(zone)}>
          {zone === "crit" && <Dot tone="crit" live />}
          {pillText}
        </Badge>
      </Panel.Head>
      <Panel.Body className="flex flex-col" style={{ gap: 14 }}>
        <Row gap="lg">
          <Gauge value={s.pressure} />
          <div className="col" style={{ gap: 3 }}>
            <span className="eyebrow">pressure</span>
            <span
              className="display"
              style={{ fontSize: 38, color: `var(--${zone})`, fontVariationSettings: "'wdth' 115,'wght' 800" }}
            >
              <AnimatedNumber value={s.pressure} format={(n) => n.toFixed(2)} />
            </span>
            <span
              className="faint"
              style={{ fontSize: 10.5, display: "inline-flex", alignItems: "center", gap: 5 }}
            >
              demand / capacity <InfoDot title={PRESSURE_HELP} />
            </span>
          </div>
        </Row>
        <div className="stack">
          <i className="ok" style={{ width: pct(leasedActive) }} />
          <i className="warn" style={{ width: pct(s.counts.cooling) }} />
          <i className="crit" style={{ width: pct(s.counts.dead) }} />
          <i className="idle" style={{ width: pct(s.free) }} />
        </div>
        <div className="legend">
          <i className="ok">leased <span className="n">{s.leased}</span></i>
          <i className="warn">cooling <span className="n">{s.counts.cooling}</span></i>
          <i className="crit">dead <span className="n">{s.counts.dead}</span></i>
          <i className="idle">free <span className="n">{s.free}</span></i>
        </div>
        <div className="kv border-t border-line pt-3">
          <dt>pool size</dt>
          <dd className="num">
            <AnimatedNumber value={s.total} format={(n) => Math.round(n).toLocaleString()} />
          </dd>
          <dt>leased</dt>
          <dd className="num text-ok">
            <AnimatedNumber value={s.leased} format={(n) => String(Math.round(n))} />
          </dd>
          <dt>free</dt>
          <dd className={s.free === 0 ? "num text-crit" : "num"}>
            <AnimatedNumber value={s.free} format={(n) => String(Math.round(n))} />
          </dd>
          <dt>acquire p50</dt>
          <dd className="num">
            {s.acquire_p50_ms == null ? "—" : <AnimatedNumber value={s.acquire_p50_ms} format={fmtMs} />}
          </dd>
          <dt>acquire p99</dt>
          <dd className="num">
            {s.acquire_p99_ms == null ? "—" : <AnimatedNumber value={s.acquire_p99_ms} format={fmtMs} />}
          </dd>
          <dt>median score</dt>
          <dd className="num">
            {s.median_score == null ? "—" : <AnimatedNumber value={s.median_score} format={fmtScore} />}
          </dd>
          <dt title="Target-side refusals (anti-bot / rate-limit) as a share of all requests. These no longer penalise proxy scores.">
            block rate
          </dt>
          <dd className={s.block_rate != null && s.block_rate >= 0.3 ? "num text-warn" : "num"}>
            {fmtPct(s.block_rate)}
          </dd>
          <dt title="Gini of leases-per-IP over the last hour, across the whole leasable pool. 0 = load spread evenly; high = the same few IPs are being hammered (IP-reputation risk).">
            spread (gini)
          </dt>
          <dd className={s.spread_gini >= 0.5 ? "num text-warn" : "num"}>
            {fmtGini(s.spread_gini)}
          </dd>
          <dt title="IPs currently benched by the per-IP velocity cap (spent their request budget for the window). They are NOT counted against capacity/pressure: they return when the window rolls.">
            velocity-capped
          </dt>
          <dd className="num">{s.velocity_capped.toLocaleString()}</dd>
        </div>
      </Panel.Body>
    </Panel>
  );
}

function StatusPill({ status }: { status: string }) {
  if (status === "active")
    return <Badge variant="outline-success"><Dot tone="ok" /> active</Badge>;
  if (status === "cooling") return <Badge variant="outline-warning">cooling</Badge>;
  if (status === "dead") return <Badge variant="outline-destructive">dead</Badge>;
  if (status === "inactive") return <Badge variant="outline">inactive</Badge>;
  return <Badge variant="outline">untried</Badge>;
}

const STATUS_FILTERS = ["all", "active", "cooling", "dead", "untried"] as const;

function ProxyProviderDeniedBanner() {
  return (
    <Panel className="mb-3.5" style={{ borderColor: "var(--crit)" }}>
      <Panel.Body>
        <Row style={{ gap: 12, alignItems: "flex-start" }}>
          <CreditCard size={18} style={{ color: "var(--crit)", flexShrink: 0, marginTop: 1 }} />
          <Stack style={{ gap: 4 }}>
            <strong style={{ color: "var(--crit)" }}>
              Proxy provider is refusing to tunnel: leasing suspended
            </strong>
            <span className="faint" style={{ fontSize: 11.5, lineHeight: 1.55 }}>
              Several IPs had their CONNECT refused by our proxy provider (Webshare) itself,
              which is a fault of the whole vendor account: <strong>402</strong> = out of
              credit or over the plan bandwidth cap, <strong>407</strong> = rejected
              credentials. Every IP in the roster bills to that one account, so none of them
              can clear it - the pool has stopped handing out leases and runs fail fast
              rather than grinding the roster down. Check the Webshare account; leasing
              resumes on its own once the refusals stop.
            </span>
          </Stack>
        </Row>
      </Panel.Body>
    </Panel>
  );
}

export function Proxies() {
  const me = useCurrentUser();
  const qc = useQueryClient();
  const notify = useToast();
  const isSuper = me.is_superuser;
  const [filter, setFilter] = useState<(typeof STATUS_FILTERS)[number]>("all");
  const [view, setView] = useState<"pool" | "usage">("pool");
  const [confirmReset, setConfirmReset] = useState(false);

  const summaryQuery = useSummaryV1ProxySummaryGet({
    query: { queryKey: ["proxy-summary"], refetchInterval: 3000 },
  });
  // Status is a server-side facet (each status fetches its own top-150); free-text
  // search over IP / platform is client-side, owned by the DataTable global filter.
  const proxiesQuery = useProxiesV1ProxyProxiesGet(
    {
      limit: 150,
      ...(filter !== "all" ? { status: filter } : {}),
    },
    { query: { queryKey: ["proxy-list", filter], refetchInterval: 5000 } },
  );
  const eventsQuery = useEventsV1ProxyEventsGet(
    { limit: 40 },
    { query: { queryKey: ["proxy-events"], refetchInterval: 4000 } },
  );

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["proxy-summary"] });
    qc.invalidateQueries({ queryKey: ["proxy-list"] });
    qc.invalidateQueries({ queryKey: ["proxy-events"] });
  };

  const sync = useSyncV1ProxySyncPost({
    mutation: {
      onSuccess: () => {
        notify("Webshare sync complete");
        invalidate();
      },
      onError: (e: Error) => notify(e.message, "crit"),
    },
  });

  // Nuclear: wipe learned per-IP state (scores/EWMAs/status) back to untried across
  // every platform, so the pool re-scores from a clean slate. Blacklisted, admin-killed,
  // and gone-from-upstream IPs survive. Guarded by a ConfirmDialog (below).
  const resetPool = useResetPoolV1ProxyResetPost({
    mutation: {
      onSuccess: (d) => {
        notify(`Pool reset: ${d.reset} IPs back to untried`);
        setConfirmReset(false);
        invalidate();
      },
      onError: (e: Error) => notify(e.message, "crit"),
    },
  });

  // The three admin actions are distinct typed endpoints; wrap them behind one
  // dispatcher so the row buttons keep a single onAction(action) contract.
  const actionOpts = (verb: string) => ({
    mutation: {
      onSuccess: (_d: unknown, v: { platform: string; ip: string }) => {
        notify(`${v.ip}: ${verb}`);
        invalidate();
      },
      onError: (e: Error) => notify(e.message, "crit"),
    },
  });
  const markDead = useMarkDeadV1ProxyProxiesPlatformIpMarkDeadPost(actionOpts("mark dead"));
  const revive = useReviveV1ProxyProxiesPlatformIpRevivePost(actionOpts("revive"));
  const resetScore = useResetScoreV1ProxyProxiesPlatformIpResetScorePost(actionOpts("reset score"));
  const actionPending = markDead.isPending || revive.isPending || resetScore.isPending;
  const runAction = (platform: Platform, ip: string, act: string) => {
    const m = act === "mark-dead" ? markDead : act === "revive" ? revive : resetScore;
    m.mutate({ platform, ip });
  };

  // Per-platform preferred proxy country codes (soft prefer) -- typed Orval hooks.
  const countryPrefQuery = useCountryPreferenceV1ProxyCountryPreferenceGet({
    query: { queryKey: ["proxy-country-pref"], refetchInterval: 15000 },
  });
  const saveCountryPref = useSetCountryPreferenceV1ProxyCountryPreferencePlatformPut({
    mutation: {
      onSuccess: (_d, v) => {
        notify(`${label(v.platform)} proxy countries updated`);
        qc.invalidateQueries({ queryKey: ["proxy-country-pref"] });
      },
      onError: (e: Error) => notify(e.message, "crit"),
    },
  });

  const platforms = summaryQuery.data?.platforms ?? [];
  const lastSync = summaryQuery.data?.last_sync;
  const proxyProviderDenied = summaryQuery.data?.proxy_provider_denied ?? false;
  const rows = proxiesQuery.data?.proxies ?? [];
  const events = eventsQuery.data?.events ?? [];
  const totalPool = platforms.reduce((a, p) => a + p.total, 0);

  const columns = useMemo<AnyColumnDef<ProxyRow>[]>(() => {
    const col = createColumnHelper<ProxyRow>();
    const cols: AnyColumnDef<ProxyRow>[] = [
      col.accessor("ip", {
        header: "IP",
        cell: ({ row }) => (
          <span className="mono" style={{ fontSize: 11.5, color: "var(--ink)" }}>{row.original.ip}</span>
        ),
        meta: { minWidth: 150 },
      }),
      col.accessor("country_code", {
        header: "Country",
        cell: ({ row }) => <span className="faint">{row.original.country_code ?? "—"}</span>,
        meta: { width: 90 },
      }),
      col.accessor("platform", {
        header: "Platform",
        cell: ({ row }) => <span className={`plat ${row.original.platform}`}>{label(row.original.platform)}</span>,
        meta: { width: 120, filterSelect: true, filterLabel: "Platform" },
      }),
      col.accessor("status", {
        header: "Status",
        cell: ({ row }) => <StatusPill status={row.original.status} />,
        meta: { width: 110 },
      }),
      col.accessor("quality_score", {
        header: "Score",
        cell: ({ row }) => fmtScore(row.original.quality_score),
        meta: { align: "right", cellClassName: "num strong", width: 90 },
      }),
      col.accessor("success_rate", {
        header: "Success%",
        cell: ({ row }) => fmtPct(row.original.success_rate),
        meta: { align: "right", cellClassName: "num", width: 100 },
      }),
      col.accessor("block_rate", {
        header: "Block%",
        cell: ({ row }) => {
          const p = row.original;
          return (
            <span
              style={p.block_rate != null && p.block_rate >= 0.3 ? { color: "var(--warn)" } : undefined}
              title={p.blocked_count ? `${p.blocked_count.toLocaleString()} target refusals` : undefined}
            >
              {fmtPct(p.block_rate)}
            </span>
          );
        },
        meta: { align: "right", cellClassName: "num faint", width: 90 },
      }),
      col.accessor("requests_in_window", {
        header: "Usage",
        cell: ({ row }) =>
          row.original.requests_in_window > 0 ? row.original.requests_in_window.toLocaleString() : "—",
        meta: { align: "right", cellClassName: "num faint", width: 90 },
      }),
      col.accessor("latency_ms", {
        header: "EWMA lat.",
        cell: ({ row }) => fmtMs(row.original.latency_ms),
        meta: { align: "right", cellClassName: "num", width: 100 },
      }),
      col.accessor("throughput_rps", {
        header: "Thru. rps",
        cell: ({ row }) => fmtRps(row.original.throughput_rps),
        meta: { align: "right", cellClassName: "num", width: 90 },
      }),
      col.accessor("holder", {
        header: "Lease holder",
        cell: ({ row }) =>
          row.original.holder ? (
            <span className="mono" style={{ fontSize: 11, color: "var(--info)" }}>{row.original.holder}</span>
          ) : (
            <span className="mono faint" style={{ fontSize: 11 }}>—</span>
          ),
        meta: { minWidth: 140 },
      }),
      col.accessor("last_released_at", {
        header: "Last release",
        cell: ({ row }) => (
          <span className="faint" style={{ fontSize: 11.5 }}>{ago(row.original.last_released_at)}</span>
        ),
        meta: { width: 120, exportValue: (p) => p.last_released_at },
      }),
    ];
    // Admin-only row actions (mark dead / revive / reset score).
    if (isSuper) {
      cols.push(
        col.display({
          id: "actions",
          header: "",
          cell: ({ row }) => (
            <RowActions
              status={row.original.status}
              busy={actionPending}
              onAction={(a) => runAction(row.original.platform, row.original.ip, a)}
            />
          ),
          meta: { align: "right", width: 130 },
          enableSorting: false,
          enableHiding: false,
        }),
      );
    }
    return cols;
    // runAction closes over stable mutation objects; re-memo on the values that
    // change what the cells render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuper, actionPending]);

  return (
    <>
      <PageHeader
        className="mb-3.5"
        title="Proxy pool"
        description="Self-hosted · Webshare · quality-scored leasing · the single egress"
        action={
          <Row gap="sm" className="flex-wrap justify-end">
            <Seg
              value={view}
              onChange={setView}
              brand
              options={[
                { value: "pool", label: "Pool" },
                { value: "usage", label: "Usage & analytics" },
              ]}
            />
            {view === "pool" && (
              <>
                <Tag>
                  <Dot tone="ok" /> last sync {lastSync ? ago(new Date(lastSync * 1000).toISOString()) : "—"}
                </Tag>
                {isSuper && (
                  <>
                    <Button variant="primary" loading={sync.isPending} onClick={() => sync.mutate()}>
                      {sync.isPending ? "Syncing…" : "Sync now"}
                    </Button>
                    <Button
                      variant="destructive"
                      loading={resetPool.isPending}
                      onClick={() => setConfirmReset(true)}
                    >
                      {resetPool.isPending ? "Resetting…" : "Reset pool"}
                    </Button>
                  </>
                )}
              </>
            )}
          </Row>
        }
      />

      {view === "usage" && <ProxyUsage />}

      {view === "pool" && (
        <>
      {summaryQuery.isError && (
        <Panel className="mb-3.5">
          <Panel.Body>
            <ErrorState
              compact
              title="Could not reach the proxy pool"
              message="Is the proxy_pool service up?"
              onRetry={() => summaryQuery.refetch()}
            />
          </Panel.Body>
        </Panel>
      )}

      {/* Above the platform cards on purpose: while this is up the cards are describing a
          roster nobody can lease from, and reading them as "the pool is fine" is the exact
          mistake that cost an hour on 2026-07-31. */}
      {proxyProviderDenied && <ProxyProviderDeniedBanner />}

      <div className="grid g-3 mb-3.5">
        {platforms.map((s) => (
          <PlatformCard key={s.platform} s={s} />
        ))}
        {platforms.length === 0 && !summaryQuery.isError && (
          <Panel>
            <Panel.Body className="muted">Loading pools…</Panel.Body>
          </Panel>
        )}
      </div>

      <CountryPreference
        data={countryPrefQuery.data}
        platforms={platforms.map((p) => p.platform)}
        isSuper={isSuper}
        saving={saveCountryPref.isPending ? saveCountryPref.variables?.platform : undefined}
        onSave={(platform, countries) =>
          saveCountryPref.mutate({ platform, data: { countries } })
        }
      />

      <Panel className="mb-3.5">
        <Panel.Head title="Proxies">
          <span className="faint num text-[11px] ml-1.5">
            {totalPool.toLocaleString()} total
          </span>
        </Panel.Head>
        <Panel.Body tight>
          <DataTable
            tableKey="proxies"
            columns={columns}
            data={rows}
            getRowId={(p) => `${p.platform}-${p.ip}`}
            globalPlaceholder="Search IP…"
            csvFilename="proxies"
            pinFirstColumn
            defaultHidden={["requests_in_window", "throughput_rps"]}
            emptyMessage="No proxies match."
            isLoading={proxiesQuery.isLoading}
            toolbarExtras={
              <Seg
                value={filter}
                onChange={setFilter}
                brand
                options={STATUS_FILTERS.map((f) => ({ value: f, label: f }))}
              />
            }
          />
        </Panel.Body>
      </Panel>

      <Panel className="scan">
        <Panel.Head title="Events">
          <Badge variant="outline-success" className="ml-1.5"><Dot live /> live</Badge>
          <div className="flex-1" />
          <span className="faint num text-[11px]">proxy reaper · lease watchdog</span>
        </Panel.Head>
        <Panel.Body className="feed">
          {events.length === 0 && <div className="muted">No events yet: start a load test.</div>}
          {events.map((e, i) => (
            <div className="ln" key={`${e.at}-${i}`}>
              <time>{parseTs(e.at).toLocaleTimeString("en-GB", { hour12: false })}</time>
              <Dot tone={e.level} />
              <b className={e.level === "crit" ? "text-crit" : e.level === "warn" ? "text-warn" : undefined}>
                {e.title}
              </b>
              <span className="faint">{e.message}</span>
              {e.tag && <Badge variant={toneVariant(e.level)} className="ml-auto">{e.tag}</Badge>}
            </div>
          ))}
        </Panel.Body>
      </Panel>
        </>
      )}

      {confirmReset && (
        <ConfirmDialog
          title="Reset proxy pool"
          tone="danger"
          confirmLabel="Reset pool"
          busy={resetPool.isPending}
          onClose={() => setConfirmReset(false)}
          onConfirm={() => resetPool.mutate({})}
        >
          Wipe all proxy scores, statuses, and leases across <b>every platform</b> and revert every
          IP to <span className="mono">untried</span>, so the pool re-scores from a clean slate.
          Blacklisted, admin-killed, and gone-from-upstream IPs are kept. This cannot be undone.
        </ConfirmDialog>
      )}
    </>
  );
}

// Styled confirmation modal (replaces window.confirm). Click-away or Cancel dismisses;
// the primary action is tinted by `tone`. Mirrors the pattern used on the Brands page.
function ConfirmDialog({
  title,
  children,
  confirmLabel,
  tone,
  busy,
  onConfirm,
  onClose,
}: {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  tone?: "danger";
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        background: "rgba(0,0,0,.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={onClose}
    >
      <Panel style={{ width: "100%", maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <Panel.Head title={title}>
          <div className="flex-1" />
          <Button variant="outline" size="sm" icon onClick={onClose} aria-label="close">✕</Button>
        </Panel.Head>
        <Panel.Body style={{ color: "var(--ink-2)", fontSize: 12.5, lineHeight: 1.5 }}>
          {children}
        </Panel.Body>
        <Panel.Head
          style={{ borderTop: "1px solid var(--line)", borderBottom: 0, justifyContent: "flex-end" }}
        >
          <Button variant="outline" disabled={busy} onClick={onClose}>Cancel</Button>
          <Button
            variant={tone === "danger" ? "destructive" : "primary"}
            disabled={busy}
            onClick={onConfirm}
            autoFocus
          >
            {busy ? "Working…" : confirmLabel}
          </Button>
        </Panel.Head>
      </Panel>
    </div>
  );
}

function RowActions({
  status,
  busy,
  onAction,
}: {
  status: string;
  busy: boolean;
  onAction: (action: string) => void;
}) {
  if (status === "dead")
    return <Button variant="success" size="sm" disabled={busy} onClick={() => onAction("revive")}>revive</Button>;
  if (status === "cooling")
    return <Button variant="outline" size="sm" disabled>cooling</Button>;
  if (status === "untried")
    return <Button variant="outline" size="sm" disabled={busy} onClick={() => onAction("reset-score")}>reset score</Button>;
  return <Button variant="destructive" size="sm" disabled={busy} onClick={() => onAction("mark-dead")}>mark dead</Button>;
}

const sameCodes = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join(",") === [...b].sort().join(",");

// Per-platform preferred proxy country codes (soft prefer). Superusers pick from the
// countries present in the pool via a multiselect; everyone else sees them read-only.
function CountryPreference({
  data,
  platforms,
  isSuper,
  saving,
  onSave,
}: {
  data: ProxyCountryPreferenceOut | undefined;
  platforms: Platform[];
  isSuper: boolean;
  saving: string | undefined;
  onSave: (platform: Platform, countries: string[]) => void;
}) {
  const prefs = data?.preferences ?? {};
  const available = data?.available ?? [];
  const [draft, setDraft] = useState<Record<string, string[]>>({});
  // A stored key outside the closed set cannot be saved back (the API answers 422), so it is not offered.
  const plats = Array.from(new Set([...platforms, ...Object.keys(prefs).filter(isPlatform)])).sort();

  const saved = (p: Platform) => prefs[p] ?? [];
  const valueFor = (p: Platform) => draft[p] ?? saved(p);
  const dirty = (p: Platform) => draft[p] !== undefined && !sameCodes(draft[p], saved(p));

  // The country selector opens an absolutely positioned menu inside this panel,
  // so for a superuser the panel has to stop clipping its own children.
  return (
    <Panel className={isSuper ? "relative z-[5] overflow-visible mb-3.5" : "mb-3.5"}>
      <Panel.Head title="Proxy country prioritization" className="flex-wrap gap-1.5">
        <span className="faint text-[11px]">
          soft prefer: preferred countries lease first, others still used as fallback
        </span>
      </Panel.Head>
      <Panel.Body>
        {plats.length === 0 ? (
          <div className="muted" style={{ fontSize: 12 }}>Loading…</div>
        ) : (
          <Stack gap="md">
            {plats.map((p) => (
              <Row key={p} gap="md" wrap>
                <span className={`plat ${p}`} style={{ minWidth: 108 }}>{label(p)}</span>
                {isSuper ? (
                  <>
                    <CountryMultiSelect
                      selected={valueFor(p)}
                      options={available}
                      disabled={saving === p}
                      onChange={(codes) => setDraft((d) => ({ ...d, [p]: codes }))}
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={!dirty(p) || saving === p}
                      onClick={() => onSave(p, valueFor(p))}
                    >
                      {saving === p ? "Saving…" : "Save"}
                    </Button>
                    {dirty(p) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDraft((d) => ({ ...d, [p]: saved(p) }))}
                      >
                        reset
                      </Button>
                    )}
                  </>
                ) : saved(p).length === 0 ? (
                  <span className="faint" style={{ fontSize: 12 }}>no preference</span>
                ) : (
                  <span className="row" style={{ gap: 5, flexWrap: "wrap" }}>
                    {saved(p).map((c) => (
                      <Badge key={c} variant="outline-primary" style={{ fontSize: 10.5 }}>{c}</Badge>
                    ))}
                  </span>
                )}
              </Row>
            ))}
          </Stack>
        )}
      </Panel.Body>
    </Panel>
  );
}

// A pick-from-the-pool multiselect: chips for the chosen ISO-2 codes + a searchable
// dropdown of the countries actually present in the proxy pool (with their counts), so
// an operator can only prefer countries that exist, no free-text typos.
function CountryMultiSelect({
  selected,
  options,
  disabled,
  onChange,
}: {
  selected: string[];
  options: CountryCount[];
  disabled?: boolean;
  onChange: (codes: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const toggle = (code: string) =>
    onChange(selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code]);

  // Every pool country, plus any already-selected code no longer present in the pool.
  const merged: CountryCount[] = [
    ...options,
    ...selected
      .filter((c) => !options.some((o) => o.country_code === c))
      .map((c) => ({ country_code: c, count: 0 })),
  ];
  const filtered = merged.filter((o) =>
    o.country_code.toLowerCase().includes(q.trim().toLowerCase()),
  );

  return (
    <div className="msel" ref={ref}>
      <div
        className={`msel-box${open ? " open" : ""}${disabled ? " disabled" : ""}`}
        onClick={() => setOpen((v) => !v)}
      >
        {selected.length === 0 && <span className="msel-ph">any country (no preference)</span>}
        {selected.map((c) => (
          <span key={c} className="msel-chip" onClick={(e) => e.stopPropagation()}>
            {c}
            <button
              type="button"
              aria-label={`remove ${c}`}
              onClick={() => toggle(c)}
            >
              ×
            </button>
          </span>
        ))}
        <span className="msel-caret">▾</span>
      </div>
      {open && (
        <div className="msel-menu">
          <input
            className="msel-search"
            autoFocus
            placeholder="search countries…"
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
          />
          <div className="msel-list">
            {filtered.length === 0 ? (
              <div className="msel-empty">no matching countries</div>
            ) : (
              filtered.map((o) => {
                const on = selected.includes(o.country_code);
                return (
                  <div
                    key={o.country_code}
                    className={`msel-opt${on ? " on" : ""}`}
                    onClick={() => toggle(o.country_code)}
                  >
                    <span className="msel-check">✓</span>
                    <span className="msel-cc">{o.country_code}</span>
                    <span className="msel-n">{o.count.toLocaleString()}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
